import { linkOptions } from "@tanstack/react-router"

import { normalizeProjectKey } from "@/lib/std.ts"
import { TaskViewId } from "@/routes/tasks/TaskViewId.ts"
import { TabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"

import type { MatterLocation } from "@/context.ts"
import type { ProjectKey, TID } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskTabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"

// Used to construct URLs with compile-time guarantees in the absence of a router
export type ExtractPlaceholders<T extends string> =
  T extends `${string}$${infer Key}/${infer Rest}`
    ? Key | ExtractPlaceholders<`/${Rest}`>
    : T extends `${string}$${infer Key}`
      ? Key
      : never

const paramRegex = /\$([a-zA-Z0-9_]+)/g

export function createPathTemplate<
  TPattern extends string,
  TKeys extends string = ExtractPlaceholders<TPattern>
>(pattern: TPattern) {
  return (params: Record<TKeys, string>) => {
    return pattern.replace(paramRegex, (_, key) => {
      const value = params[key as TKeys]
      if (!value) throw new Error(`Missing value for ${key}`)
      return value
    })
  }
}

export const Navigator = {
  HomePattern: "/",
  LoginPattern: "/~/login",
  InvitePattern: "/~/invite",
  OrgHomePattern: "/$orgId",
  NewProjectPattern: "/$orgId/p/new",
  UseSampleProjectPattern: "/$orgId/p/useSample/$project",
  ProjectPattern: "/$orgId/p/$projectKey/t",
  TaskPattern: "/$orgId/p/$projectKey/t/$taskId",
  NewTaskPattern: "/$orgId/p/$projectKey/t/new",
  UserProfilePattern: "/$orgId/user/$username",
  UserSettingsPattern: "/$orgId/settings/$tab",
  ProjectSettingsPattern: "/$orgId/p/$projectKey/settings/$tab",

  home: (params?: HomeParams) => linkOptions({
    to: Navigator.HomePattern,
    replace: params?.replace,
  }),

  login: (params?: HomeParams) => linkOptions({
    to: Navigator.LoginPattern,
    replace: params?.replace,
  }),

  invite: (token: string, orgId: string) => linkOptions({
    to: Navigator.InvitePattern,
    search: { token, orgId }
  }),

  orgHome: (orgId: string, params?: HomeParams) => linkOptions({
    to: Navigator.OrgHomePattern,
    params: { orgId },
    replace: params?.replace,
    reloadDocument: params?.reloadDocument
  }),

  newProject: (orgId: string, params: PagePopupParams) => linkOptions({
    to: Navigator.NewProjectPattern,
    params: { orgId },
    state: { closeLocation: params.closeLocation }
  }),

  project: (orgId: string, projectKey: ProjectKey) => linkOptions({
    to: Navigator.ProjectPattern,
    params: { orgId, projectKey: normalizeProjectKey(projectKey) }
  }),

  task: (orgId: string, projectKey: ProjectKey, taskId: string, params?: TaskParams) => linkOptions({
    to: Navigator.TaskPattern,
    params: { orgId, projectKey: normalizeProjectKey(projectKey), taskId },
    search: { view: params?.view, tab: params?.tab }
  }),

  newTask: (orgId: string, projectKey: ProjectKey, params: NewTaskParams) => linkOptions({
    to: Navigator.NewTaskPattern,
    params: { orgId, projectKey: normalizeProjectKey(projectKey) },
    search: { onboarding: params?.onboarding },
    state: { closeLocation: params.closeLocation }
  }),

  user: (orgId: string, username: string) => linkOptions({
    to: Navigator.UserProfilePattern,
    params: { orgId, username }
  }),

  settings: (orgId: string, tab: string) => linkOptions({
    to: Navigator.UserSettingsPattern,
    params: { orgId, tab }
  }),

  projectSettings: (orgId: string, projectKey: ProjectKey, tab: string) => linkOptions({
    to: Navigator.ProjectSettingsPattern,
    params: { orgId, projectKey: normalizeProjectKey(projectKey), tab }
  })
} as const

export function initTaskLocation(orgId: string, projectKey: ProjectKey, taskId: TID, view: TaskViewId = TaskViewId.Chat) {
  return Navigator.task(orgId, projectKey, taskId, {
    view: view,
    tab: TabId.RuntimeConfigFile
  })
}

type HomeParams = {
  replace?: boolean
  reloadDocument?: boolean
}

type TaskParams = {
  view?: TaskViewId
  tab?: TaskTabId
}

type NewTaskParams = PagePopupParams & {
  onboarding?: boolean
}

type PagePopupParams = {
  closeLocation: MatterLocation
}